import { type KeyboardEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "./souvenir.ts";
import { ItemIcon } from "./ui.tsx";

// One of the map's filters folded into a chip that opens a menu of one choice each, like What's
// (features/map.md, "Whose wants, for any group size"): the map's Who (whose wants show) and, on a
// computer, Friends' picks. Picking one closes it; Escape or a tap elsewhere closes it too. It opens under
// the chip, kept on the screen and scrolling inside when long, with "Find someone" at its top when asked.

export interface Choice {
  value: string;
  // What its row in the menu says.
  label: string;
  // What the chip says while it's the one on, where that's shorter ("Zoë" for Zoë Fairchild) or other
  // ("Friends' picks" for Everything); else the label.
  short?: string;
  // Its face or icon, at the row's start; the chip shows it too.
  lead: ReactNode;
  // How many it has (a friend's picks), at the row's end.
  count?: number;
}

export function ChoiceFilter({
  title,
  legend,
  choices,
  value,
  compact,
  find,
  onPick,
  className,
}: {
  // The chip's name ("Who"), said before the choice ("Who: Zoë Fairchild").
  title: string;
  // The menu's name ("Who to show").
  legend: string;
  // The first choice is the one that filters nothing (Everyone, Everything): the chip looks selected for any
  // other.
  choices: Choice[];
  value: string;
  // On a phone the chip keeps one width, its label ending in an ellipsis, so the filters keep to two rows.
  compact: boolean;
  // A search at the menu's top, and what it finds: the choices whose values it returns.
  find?: { placeholder: string; finds: (value: string, query: string) => boolean; none: (query: string) => string };
  onPick: (value: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLFieldSetElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const on = choices.find((c) => c.value === value) ?? choices[0];
  const searching = Boolean(find) && query.trim() !== "";
  const shown = find && searching ? choices.filter((c) => find.finds(c.value, query)) : choices;

  const close = (refocus: boolean) => {
    setOpen(false);
    setQuery("");
    if (refocus) chip.current?.focus();
  };
  const pick = (v: string) => {
    onPick(v);
    close(true);
  };

  // Closes on a tap or click anywhere else, and on Escape: this first, before the map's list or card
  // (caught on the way down, so the trip's own Escape never hears it). Moving focus out of it closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !root.current?.contains(e.target as Node) && close(false);
    // A search with something in it is cleared first.
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (document.activeElement === input.current && input.current?.value) setQuery("");
      else close(true);
    };
    const left = (e: FocusEvent) => {
      const to = e.relatedTarget as Node | null;
      if (to && !root.current?.contains(to)) close(false);
    };
    const el = root.current;
    addEventListener("pointerdown", away);
    addEventListener("keydown", onEscape, { capture: true });
    el?.addEventListener("focusout", left);
    return () => {
      removeEventListener("pointerdown", away);
      removeEventListener("keydown", onEscape, { capture: true });
      el?.removeEventListener("focusout", left);
    };
  });

  // Kept on the screen: shifted left when the chip sits near the right edge, and no taller than the room
  // under it (above the tabs on a phone), scrolling inside beyond that. Measured on every render, as the
  // chip moves when its label or the row it wraps onto changes. As it opens, the one on is brought into
  // view: a person far down a long menu is shown in its middle.
  const placed = useRef(false);
  useLayoutEffect(() => {
    const el = menu.current;
    if (!open || !chip.current || !el) {
      placed.current = false;
      return;
    }
    const box = chip.current.getBoundingClientRect();
    const shift = Math.max(0, box.left + el.offsetWidth - (document.documentElement.clientWidth - 16));
    const tabs = document.querySelector('nav[aria-label="Views"]')?.getBoundingClientRect();
    const floor = tabs && tabs.top > box.bottom ? tabs.top : document.documentElement.clientHeight;
    el.style.transform = `translateX(${-shift}px)`;
    el.style.maxHeight = `${Math.max(160, Math.floor(floor - 16 - (box.bottom + 8)))}px`;
    if (placed.current) return;
    placed.current = true;
    const scroller = list.current;
    const row = scroller?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!scroller || !row) return;
    const area = scroller.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top < area.top || r.bottom > area.bottom)
      scroller.scrollTop += r.top + r.height / 2 - (area.top + area.height / 2);
  });

  // Opened on a computer, the search takes the keys at once; on a phone it waits for a tap, so the
  // keyboard doesn't cover the menu as it opens.
  useEffect(() => {
    if (open && !compact) input.current?.focus();
  }, [open, compact]);

  // The arrows move between the search and the rows, Home and End to the ends.
  const rows = () => [...(list.current?.querySelectorAll<HTMLElement>("button") ?? [])];
  const onKey = (e: KeyboardEvent<HTMLFieldSetElement>) => {
    const all = rows();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const inField = document.activeElement === input.current;
    let to: number | null = null;
    if (e.key === "ArrowDown") to = inField ? 0 : Math.min(at + 1, all.length - 1);
    else if (e.key === "ArrowUp") {
      if (at === 0 && input.current) {
        e.preventDefault();
        input.current.focus();
        return;
      }
      to = at > 0 ? at - 1 : null;
    } else if ((e.key === "Home" || e.key === "End") && !inField) to = e.key === "Home" ? 0 : all.length - 1;
    const target = to === null ? undefined : all[to];
    if (!target) return;
    e.preventDefault();
    target.focus();
  };

  if (!on) return null;
  const filtering = on !== choices[0];
  return (
    <div className={`tm-choice-filter${className ? ` ${className}` : ""}`} ref={root}>
      <button
        ref={chip}
        type="button"
        className={`sv-chip sv-chip--pick tm-choice-chip${filtering ? " is-selected" : ""}${compact ? " is-compact" : ""}`}
        aria-label={`${title}: ${on.label}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => (open ? close(false) : setOpen(true))}
        // Down from the chip, into the open menu: its search, or the one on.
        onKeyDown={(e) => {
          if (e.key !== "ArrowDown" || !open) return;
          e.preventDefault();
          (input.current ?? list.current?.querySelector<HTMLElement>('[aria-pressed="true"]'))?.focus();
        }}
      >
        <span className="tm-choice-lead" aria-hidden="true">
          {on.lead}
        </span>
        <span className="tm-choice-chip-label">{on.short ?? on.label}</span>
        {!compact && <Icon name="chevron" size={14} className={`tm-kind-caret${open ? " is-open" : ""}`} />}
      </button>
      {open && (
        <fieldset className="tm-choice-menu" id={id} ref={menu} onKeyDown={onKey}>
          <legend className="tm-hidden">{legend}</legend>
          {find && (
            <search className="tm-search tm-choice-find">
              <ItemIcon name="search" size={20} />
              <input
                ref={input}
                type="search"
                className="body"
                enterKeyHint="go"
                autoComplete="off"
                spellCheck={false}
                placeholder={find.placeholder}
                aria-label={find.placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searching && shown.length === 1 && shown[0]) {
                    e.preventDefault();
                    pick(shown[0].value);
                  }
                }}
              />
              {query !== "" && (
                <button
                  type="button"
                  className="tm-search-clear"
                  aria-label="Clear the search"
                  onClick={() => {
                    setQuery("");
                    input.current?.focus();
                  }}
                >
                  <Icon name="close" size={16} />
                </button>
              )}
            </search>
          )}
          {find && (
            <span className="tm-hidden" aria-live="polite">
              {searching ? `${shown.length} found` : ""}
            </span>
          )}
          <div className="tm-choice-list" ref={list}>
            {shown.map((c) => (
              <button
                key={c.value}
                type="button"
                className="tm-choice-row"
                aria-pressed={c.value === on.value}
                onClick={() => pick(c.value)}
              >
                <span className="tm-choice-lead" aria-hidden="true">
                  {c.lead}
                </span>
                <span className="callout tm-choice-row-label">{c.label}</span>
                {c.count !== undefined && <span className="callout tm-muted">{c.count}</span>}
                {c.value === on.value && <Icon name="check" size={16} className="tm-choice-check" />}
              </button>
            ))}
            {shown.length === 0 && find && <p className="callout tm-muted tm-choice-none">{find.none(query.trim())}</p>}
          </div>
        </fieldset>
      )}
    </div>
  );
}
